import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import WhatsAppButton from '@/components/feature/WhatsAppButton';
import { useScrollAnimation } from '@/hooks/useScrollAnimation';

const services = [
  {
    icon: 'ri-building-2-line',
    title: 'Galpones Industriales',
    desc: 'Diseñamos y construimos galpones metálicos de alta resistencia para uso industrial, agrícola, logístico y de almacenamiento. Nuestros galpones industriales se adaptan a cualquier terreno y necesidad en todo Chile.',
    features: [
      'Estructuras metálicas de acero galvanizado',
      'Cubiertas y revestimientos de alta durabilidad',
      'Sistemas de ventilación e iluminación natural',
      'Puertas industriales y accesos vehiculares',
      'Fundaciones y obras civiles incluidas',
      'Proyectos desde 200 m² hasta grandes superficies',
    ],
    img: 'https://public.readdy.ai/ai/img_res/edited_709c45eb2f511fccb8514bc1e88dda5f_49a5c4f4.jpg',
  },
  {
    icon: 'ri-store-2-line',
    title: 'Locales Comerciales',
    desc: 'Construimos locales comerciales modernos y funcionales para retail, restaurantes, oficinas y más. Diseños adaptados a la imagen de cada negocio con acabados de alta calidad.',
    features: [
      'Diseño arquitectónico personalizado',
      'Fachadas modernas y atractivas',
      'Instalaciones eléctricas y sanitarias',
      'Acabados interiores de calidad',
      'Cumplimiento de normativas comerciales',
      'Entrega llave en mano',
    ],
    img: 'https://i.imgur.com/tl7d4Yb.jpg',
  },
  {
    icon: 'ri-road-map-line',
    title: 'Obras Civiles',
    desc: 'Ejecutamos obras civiles en general: fundaciones, estructuras de hormigón, pavimentación, muros de contención y más. Experiencia en proyectos de gran envergadura en todo Chile.',
    features: [
      'Fundaciones y excavaciones',
      'Estructuras de hormigón armado',
      'Pavimentación y obras viales',
      'Muros de contención y taludes',
      'Redes de agua y alcantarillado',
      'Movimiento de tierras',
    ],
    img: 'https://public.readdy.ai/ai/img_res/edited_93665d4c5098f15ab9c39263b76e7bda_41776496.jpg',
  },
  {
    icon: 'ri-tools-line',
    title: 'Proyectos Estructurales',
    desc: 'Desarrollamos proyectos estructurales complejos con ingeniería de alto nivel. Desde el diseño hasta la ejecución, garantizamos estructuras seguras y duraderas.',
    features: [
      'Ingeniería estructural certificada',
      'Cálculo y diseño de estructuras',
      'Estructuras metálicas y mixtas',
      'Refuerzo y rehabilitación estructural',
      'Supervisión técnica especializada',
      'Certificaciones y permisos',
    ],
    img: 'https://readdy.ai/api/search-image?query=structural%20engineering%20project%20Chile%2C%20steel%20frame%20construction%20building%2C%20engineers%20reviewing%20plans%20on%20site%2C%20professional%20photography%2C%20industrial%20background%2C%20high%20quality%20photorealistic&width=700&height=450&seq=svc004&orientation=landscape',
  },
  {
    icon: 'ri-water-flash-line',
    title: 'Pisciculturas',
    desc: 'Construcción especializada de instalaciones para pisciculturas y acuicultura. Infraestructura robusta adaptada a las exigentes condiciones de Chile.',
    features: [
      'Estanques y piscinas de cultivo',
      'Sistemas de recirculación de agua',
      'Galpones para procesamiento',
      'Instalaciones de bombeo',
      'Obras civiles especializadas',
      'Adaptación a terrenos difíciles',
    ],
    img: 'https://readdy.ai/api/search-image?query=close-up%20professional%20photography%20of%20modern%20aquaculture%20fish%20farm%20facility%2C%20large%20circular%20concrete%20fish%20cultivation%20tanks%20filled%20with%20clear%20blue%20water%20arranged%20in%20neat%20rows%2C%20steel%20roof%20structure%20overhead%2C%20industrial%20aquaculture%20center%20in%20Chile%2C%20dramatic%20cinematic%20lighting%20with%20deep%20blue%20water%20tones%2C%20premium%20corporate%20photography%20style%2C%20highly%20detailed%20photorealistic%2C%20clean%20organized%20facility&width=700&height=450&seq=svc005new&orientation=landscape',
  },
  {
    icon: 'ri-hammer-line',
    title: 'Fabricación de Estructuras Metálicas',
    desc: 'Fabricamos estructuras metálicas a medida para galpones, naves industriales, cubiertas y proyectos especiales. Producción en taller con control de calidad y montaje profesional en obra.',
    features: [
      'Fabricación de columnas y vigas de acero',
      'Cubiertas y revestimientos metálicos',
      'Estructuras para galpones y naves',
      'Soldadura certificada y control de calidad',
      'Montaje y ensamblaje en obra',
      'Diseño según carga sísmica chilena',
    ],
    img: 'https://storage.readdy-site.link/project_files/e9e9255d-4fa4-42a2-878c-87b7e92a4901/bb261f02-579f-4668-9b0f-860f63c2a3f0_Casa-matriz-Ingemetalsur-4.jpg?v=b1036c560cf9aed63ae2aae75f7df189',
  },
];

const reveal = (r: ReturnType<typeof useScrollAnimation>) =>
  `reveal ${r.isVisible ? 'reveal-visible' : ''}`;
const revealLeft = (r: ReturnType<typeof useScrollAnimation>) =>
  `reveal-left ${r.isVisible ? 'reveal-visible' : ''}`;
const revealRight = (r: ReturnType<typeof useScrollAnimation>) =>
  `reveal-right ${r.isVisible ? 'reveal-visible' : ''}`;
const revealScale = (r: ReturnType<typeof useScrollAnimation>) =>
  `reveal-scale ${r.isVisible ? 'reveal-visible' : ''}`;

const ServiciosPage = () => {
  const heroR = useScrollAnimation();
  const processR = useScrollAnimation();
  const servicesR = useScrollAnimation();
  const ctaR = useScrollAnimation();

  return (
    <>
      <Navbar />
      <main>
        {/* Hero */}
        <section className="relative pt-32 pb-20 overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://readdy.ai/api/search-image?query=modern%20industrial%20steel%20warehouse%20construction%20site%20Chile%2C%20metal%20structure%20framework%20under%20blue%20sky%20with%20warm%20sunlight%2C%20wide%20angle%20professional%20photography%2C%20clean%20corporate%20aesthetic%2C%20bright%20and%20clear%20industrial%20architecture%2C%20premium%20quality%20photorealistic&width=1920&height=600&seq=svcHero002&orientation=landscape"
              alt="Servicios de construcción IngemetalSur - Galpones industriales y obras civiles Chile"
              className="w-full h-full object-cover object-center opacity-90"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-[#0C1D2E]/70 via-[#0C1D2E]/50 to-[#0C1D2E]/20"></div>
          </div>
          <div ref={heroR.ref} className={`relative z-10 max-w-7xl mx-auto px-4 md:px-8 ${reveal(heroR)}`}>
            <div className="max-w-2xl">
              <span className="text-[#C88A2A] text-sm font-bold uppercase tracking-widest">Nuestros servicios</span>
              <h1 className="text-4xl md:text-5xl font-black text-white mt-2 mb-4 leading-tight">
                Soluciones constructivas<br />
                <span className="text-[#C88A2A]">integrales</span>
              </h1>
              <p className="text-slate-200 text-lg leading-relaxed">
                Más de 25 años ejecutando proyectos de construcción en todo Chile. Galpones industriales, obras civiles, locales comerciales y más.
              </p>
            </div>
          </div>
        </section>

        {/* Process — arriba de Especialidades */}
        <section className="py-20 bg-[#F8FAFC]">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <div ref={processR.ref} className={`text-center mb-14 ${reveal(processR)}`}>
              <span className="text-[#C88A2A] text-sm font-bold uppercase tracking-widest">Proceso de trabajo</span>
              <h2 className="text-3xl md:text-4xl font-black text-[#0C1D2E] mt-2">
                ¿Cómo trabajamos?
              </h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                { step: '01', icon: 'ri-chat-3-line', title: 'Consulta inicial', desc: 'Nos contactas y conversamos sobre tu proyecto, necesidades y presupuesto.' },
                { step: '02', icon: 'ri-file-list-3-line', title: 'Cotización', desc: 'Elaboramos una cotización detallada y transparente sin costo ni compromiso.' },
                { step: '03', icon: 'ri-draft-line', title: 'Planificación', desc: 'Diseñamos el proyecto, obtenemos permisos y planificamos la ejecución.' },
                { step: '04', icon: 'ri-building-line', title: 'Construcción', desc: 'Ejecutamos la obra con nuestro equipo profesional, cumpliendo plazos y calidad.' },
              ].map((step, i) => (
                <div key={step.step} className={`bg-white rounded-lg p-7 text-center hover:shadow-lg hover:-translate-y-1 transition-all duration-300 reveal ${processR.isVisible ? 'reveal-visible' : ''}`} style={{ transitionDelay: `${i * 120}ms` }}>
                  <div className="text-[#C88A2A]/20 font-black text-5xl mb-3">{step.step}</div>
                  <div className="w-12 h-12 flex items-center justify-center bg-[#C88A2A] rounded-full mx-auto mb-4 hover:scale-110 transition-transform duration-200">
                    <i className={`${step.icon} text-white text-xl`}></i>
                  </div>
                  <h3 className="font-bold text-[#0C1D2E] text-base mb-2">{step.title}</h3>
                  <p className="text-[#475569] text-sm leading-relaxed">{step.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Services Grid */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <div ref={servicesR.ref} className={`text-center mb-14 ${reveal(servicesR)}`}>
              <span className="text-[#C88A2A] text-sm font-bold uppercase tracking-widest">Especialidades</span>
              <h2 className="text-3xl md:text-4xl font-black text-[#0C1D2E] mt-2 mb-4">
                ¿En qué podemos ayudarte?
              </h2>
              <p className="text-[#475569] text-base max-w-xl mx-auto">
                Ofrecemos soluciones constructivas completas para empresas y particulares en todo Chile.
              </p>
            </div>

            <div className="space-y-16">
              {services.map((service, i) => (
                <div
                  key={service.title}
                  className={`grid grid-cols-1 lg:grid-cols-2 gap-10 items-center reveal ${servicesR.isVisible ? 'reveal-visible' : ''}`}
                  style={{ transitionDelay: `${i * 150}ms` }}
                >
                  <div className={i % 2 !== 0 ? 'lg:order-2' : ''}>
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-10 h-10 flex items-center justify-center bg-[#C88A2A] rounded-md hover:scale-110 transition-transform duration-200">
                        <i className={`${service.icon} text-white text-xl`}></i>
                      </div>
                      <h3 className="text-2xl font-black text-[#0C1D2E]">{service.title}</h3>
                    </div>
                    <p className="text-[#475569] text-base leading-relaxed mb-6">{service.desc}</p>
                    <ul className="space-y-2.5 mb-8">
                      {service.features.map((f, fi) => (
                        <li key={f} className={`flex items-center gap-3 reveal-delay-${(fi + 1) * 50}`}>
                          <div className="w-5 h-5 flex items-center justify-center shrink-0">
                            <i className="ri-check-line text-[#C88A2A] text-base font-bold"></i>
                          </div>
                          <span className="text-[#475569] text-sm">{f}</span>
                        </li>
                      ))}
                    </ul>
                    <a
                      href="https://wa.me/56992265118?text=Hola%2C%20me%20interesa%20cotizar%20el%20servicio%20de%20"
                      target="_blank"
                      rel="nofollow noopener noreferrer"
                      className="inline-flex items-center gap-2 bg-[#C88A2A] hover:bg-[#A06B1A] hover:scale-[1.02] active:scale-[0.98] text-white font-bold px-6 py-3 rounded-md transition-all duration-200 whitespace-nowrap text-sm"
                    >
                      <i className="ri-whatsapp-line"></i>
                      Cotizar este servicio
                    </a>
                  </div>
                  <div className={i % 2 !== 0 ? 'lg:order-1' : ''}>
                    <img
                      src={service.img}
                      alt={`${service.title} - IngemetalSur constructora Chile`}
                      className="w-full h-72 object-cover object-center bg-gray-50 rounded-lg hover:shadow-xl hover:scale-[1.01] transition-all duration-500"
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 bg-[#C88A2A]">
          <div ref={ctaR.ref} className={`max-w-3xl mx-auto px-4 md:px-8 text-center ${revealScale(ctaR)}`}>
            <h2 className="text-2xl md:text-3xl font-black text-white mb-4">
              ¿Necesitas una cotización?
            </h2>
            <p className="text-white/70 text-base mb-8">
              Contáctanos hoy y recibe una propuesta personalizada para tu proyecto de construcción en cualquier parte de Chile.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="https://wa.me/56992265118?text=Hola%2C%20me%20interesa%20solicitar%20una%20cotizaci%C3%B3n"
                target="_blank"
                rel="nofollow noopener noreferrer"
                className="inline-flex items-center justify-center gap-3 bg-[#25D366] hover:bg-[#1da851] hover:scale-[1.02] active:scale-[0.98] text-white font-bold px-8 py-4 rounded-md transition-all duration-200 whitespace-nowrap"
              >
                <i className="ri-whatsapp-line text-xl"></i>
                WhatsApp
              </a>
              <a
                href="tel:+56992265118"
                className="inline-flex items-center justify-center gap-3 bg-white/10 hover:bg-white/20 hover:scale-[1.02] active:scale-[0.98] border border-white/30 text-white font-bold px-8 py-4 rounded-md transition-all duration-200 whitespace-nowrap"
              >
                <i className="ri-phone-line text-xl"></i>
                +56 9 9226 5118
              </a>
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <WhatsAppButton />
    </>
  );
};

export default ServiciosPage;